// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { InvoiceDetail } from '@/billing/api/types';
import { UpdateInvoiceInput } from '@/billing/api/update-invoice';
import { InvoiceEditForm } from '@/billing/components/invoice-edit-form';
import { useUpdateInvoice } from '@/billing/hooks/use-update-invoice';
import { InvoiceEditFormData } from '@/billing/schemas/invoice-edit-form-schema';
import {
  isCustomPaymentTerm,
  makeCustomPaymentTerm,
} from '@/billing/utilities/custom-payment-term';
import { SupportSnackbar } from '@/components/support-snackbar';
import { useToast } from '@/contexts/toast-context';
import { getCustomers } from '@/customer/api/get-customers';
import { getPaymentTerms } from '@/payment-term/api/get-payment-terms';
import { PaymentTerm } from '@/payment-term/api/types';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

interface InvoiceEditDialogProps {
  /**
   * Whether the dialog is open.
   */
  isOpen: boolean;

  /**
   * Callback fired when the dialog is closed.
   */
  onClose: () => void;

  /**
   * The invoice to edit.
   */
  invoice: InvoiceDetail;
}

const FORM_ID = 'invoice-edit-form';

// Nullable text fields use "" in the form. Normalize to null when sending
// to the API so the backend either persists the new value or clears the
// stored value.
function emptyToNull(value: string): string | null {
  return value.trim() === '' ? null : value;
}

/**
 * Dialog for editing the header fields of a DRAFT invoice.
 * Bundles dates, payment terms, bill from/to, and PO number so users can
 * edit everything at once instead of one inline field at a time.
 */
export function InvoiceEditDialog(props: InvoiceEditDialogProps) {
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [invoiceNumberError, setInvoiceNumberError] = useState<string | null>(
    null
  );
  const { showToast } = useToast();

  // Shares the cache key with the parent route's prefetch so the dialog
  // reads payment terms straight from the cache when it opens.
  const paymentTermsQuery = useQuery({
    queryKey: ['payment-term'],
    queryFn: getPaymentTerms,
  });

  const customersQuery = useQuery({
    queryKey: ['customer'],
    queryFn: () => getCustomers(),
  });
  const customersData = customersQuery.data;
  const customers = useMemo(
    () => customersData?.customers ?? [],
    [customersData]
  );

  // Resolve the customer currently assigned to the invoice by matching its
  // key against the fetched customer list. Returns null when the invoice has
  // no customer.
  const initialCustomer = useMemo(() => {
    if (props.invoice.customerKey === null) return null;
    return customers.find((c) => c.key === props.invoice.customerKey) ?? null;
  }, [customers, props.invoice.customerKey]);

  // Resolve the payment term currently persisted on the invoice by matching
  // its label against the fetched list. When there is no match but the
  // invoice has a non-null label, fall back to a "Custom" sentinel so the
  // existing label is visible in the dropdown. Returns null only when the
  // invoice has no payment term set at all.
  const initialPaymentTerm = useMemo<PaymentTerm | null>(() => {
    const list = paymentTermsQuery.data?.paymentTerms ?? [];
    const label = props.invoice.paymentTerms;
    if (label === null) return null;
    const match = list.find((term) => term.label === label);
    if (match) return match;
    return makeCustomPaymentTerm(label);
  }, [paymentTermsQuery.data, props.invoice.paymentTerms]);

  // The full option list shown in the dropdown: the fetched payment terms,
  // optionally prefixed by the Custom sentinel when the invoice currently
  // has an unrecognized label. Including the sentinel in options is what
  // suppresses the MUI "value not in options" warning.
  const paymentTermOptions = useMemo<PaymentTerm[]>(() => {
    const list = paymentTermsQuery.data?.paymentTerms ?? [];
    if (isCustomPaymentTerm(initialPaymentTerm)) {
      return [initialPaymentTerm as PaymentTerm, ...list];
    }
    return list;
  }, [paymentTermsQuery.data, initialPaymentTerm]);

  const mutation = useUpdateInvoice({
    invoiceKey: props.invoice.key,
    onSuccess: () => {
      showToast({
        element: (
          <Typography>
            Invoice <b>{props.invoice.invoiceNumber}</b> updated
          </Typography>
        ),
        severity: 'success',
        duration: 3000,
      });
      props.onClose();
    },
    onError: (message, apiStatusCode, httpStatusCode) => {
      // Field-mappable known code → inline helper text on Invoice number,
      // dialog stays open so the user can rename and retry.
      if (apiStatusCode === ERROR_CODE_MAPPING.invoice_number_duplicate) {
        setInvoiceNumberError(message);
        return;
      }
      // 404 means the invoice is gone; close the dialog — there's nothing
      // to save. Parent detail route will handle surfacing the navigation.
      if (httpStatusCode === 404) {
        props.onClose();
        return;
      }
      // Unexpected errors (5xx / unknown) keep the dialog open so the
      // user's edits aren't lost; the SupportSnackbar renders above.
      setShowSnackbar(true);
    },
  });

  const handleAfterClose = useCallback(() => {
    setShowSnackbar(false);
    setInvoiceNumberError(null);
    mutation.reset();
  }, [mutation]);

  const handleFormSubmit = useCallback(
    (data: InvoiceEditFormData) => {
      setInvoiceNumberError(null);

      // Only send customerKey when the user actually changed the customer.
      // Using undefined omits the field from the JSON payload so the backend
      // treats it as "not sent" (sparse update no-op). This prevents silent
      // customer clearing when the invoice's customer isn't in the dropdown
      // list (e.g. archived or deleted).
      const customerChanged =
        (data.customer?.id ?? null) !== (initialCustomer?.id ?? null);
      const invoiceNumberChanged =
        data.invoiceNumber !== props.invoice.invoiceNumber;

      const payload: UpdateInvoiceInput = {
        invoiceKey: props.invoice.key,
        invoiceNumber: invoiceNumberChanged ? data.invoiceNumber : undefined,
        customerKey: customerChanged ? (data.customer?.key ?? null) : undefined,
        invoiceDate: data.invoiceDate,
        dueDate: data.dueDate,
        paymentTerms: data.paymentTerm?.label ?? null,
        billFromName: data.billFromName,
        billFromAddress: emptyToNull(data.billFromAddress),
        billToCompanyName: emptyToNull(data.billToCompanyName),
        billingContactName: emptyToNull(data.billingContactName),
        billToAddress: emptyToNull(data.billToAddress),
        billToPhone: emptyToNull(data.billToPhone),
        billToEmail: emptyToNull(data.billToEmail),
        purchaseOrderNumber: emptyToNull(data.purchaseOrderNumber),
      };
      mutation.mutate(payload);
    },
    [initialCustomer, mutation, props.invoice.key, props.invoice.invoiceNumber]
  );

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={props.isOpen}
      onClose={props.onClose}
      scroll="paper"
      TransitionProps={{
        onExited: handleAfterClose,
      }}
    >
      <DialogTitle>Edit invoice</DialogTitle>
      <DialogContent dividers>
        {paymentTermsQuery.isPending || customersQuery.isPending ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <InvoiceEditForm
            invoice={props.invoice}
            initialPaymentTerm={initialPaymentTerm}
            initialCustomer={initialCustomer}
            customers={customers}
            paymentTermOptions={paymentTermOptions}
            onSubmit={handleFormSubmit}
            formId={FORM_ID}
            isDisabled={mutation.isPending}
            isOpen={props.isOpen}
            invoiceNumberError={invoiceNumberError}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button
          type="submit"
          form={FORM_ID}
          variant="contained"
          color="primary"
          disabled={mutation.isPending}
          startIcon={
            mutation.isPending ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          Save changes
        </Button>
      </DialogActions>
      <SupportSnackbar
        isOpen={showSnackbar}
        onClose={() => setShowSnackbar(false)}
        message="An error occurred updating the invoice."
      />
    </Dialog>
  );
}
